import { useEffect, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'

import {
  FunnelRule,
  INITIAL_PAGINATION_RESPONSE,
  PrivacyLevel,
  useGetFunnelRuleGroupListQuery,
  useGetFunnelRuleListQuery,
} from '@shared/api/rtkQuery'
import InfiniteScrollLoader from '@shared/ui/loaders/InfiniteScrollLoader'

import {
  GROUP_UPPER_BOUNDARY,
  LIST_PER_PAGE,
  SCROLL_DEBOUNCE_TIME,
} from './_shared/constants'
import Empty from './_shared/Empty'
import { ChartWrapper, Content } from './_shared/Style'
import FunnelChart from './FunnelChart'

type Props = {
  privacyLevel: PrivacyLevel
  activeGroupId: number
}

const FunnelList = ({ privacyLevel, activeGroupId }: Props) => {
  const [page, setPage] = useState<number>(1)
  const [data, setData] = useState<FunnelRule[]>([])

  const {
    data: listData = INITIAL_PAGINATION_RESPONSE,
    isLoading,
    isSuccess,
  } = useGetFunnelRuleListQuery({
    page,
    perPage: LIST_PER_PAGE,
    privacyLevel,
    groupId: activeGroupId,
  })
  const { groupListData } = useGetFunnelRuleGroupListQuery(
    { funnelRuleType: privacyLevel, page: 1, perPage: GROUP_UPPER_BOUNDARY },
    {
      selectFromResult: ({ data = [] }) => {
        return {
          groupListData: data.map(group => ({
            ...group,
            disabled: group.id === activeGroupId,
          })),
        }
      },
    }
  )

  const pageLimit = listData.totalCount / LIST_PER_PAGE
  const hasMore = page < pageLimit && listData.totalCount > 0
  const isFirstFetching = isLoading && listData.items.length === 0

  useEffect(() => {
    setData(prev => {
      const filteredData = listData.items.filter(
        item => !prev.find(d => d.id === item.id)
      )

      return [...prev, ...filteredData]
    })
  }, [listData.items])

  const handleScrollWithDebounce = useMemo(
    () =>
      debounce(() => {
        if (hasMore) {
          setPage(page + 1)
        }
      }, SCROLL_DEBOUNCE_TIME),
    [hasMore, page]
  )

  const handleDeleteItemFromList = (id: number) => {
    setData(prev => prev.filter(d => d.id !== id))
  }

  return (
    <>
      <Content>
        {data.map(({ id, title, dashboardConfig, filter }) => (
          <ChartWrapper key={`funnel_${privacyLevel}_${id}`}>
            <FunnelChart
              funnelId={id}
              title={title}
              chartType={dashboardConfig.chartType}
              filter={filter}
              groupList={groupListData}
              updateList={handleDeleteItemFromList}
            />
          </ChartWrapper>
        ))}
      </Content>
      {!isLoading && data.length === 0 && (
        <Empty
          analyticsType="funnel"
          privacyLevel={privacyLevel}
          groupId={activeGroupId}
        />
      )}

      {((isSuccess && hasMore) || isFirstFetching) && (
        <InfiniteScrollLoader
          isLoading={isLoading}
          loadMore={handleScrollWithDebounce}
        />
      )}
    </>
  )
}

export default FunnelList
